import { SxProps, TablePagination } from '@mui/material'
import { C1TableReduxStyles } from 'components/core/list/C1Table/C1TableReduxStyles'
import { FC, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

export interface C1TablePaginationProps {
    page: number
    pageSize: number
    data: ReadonlyArray<ReadonlyArray<unknown>>
    canLoadMore?: boolean
    isLoading?: boolean
    onPageChange: (newPage: number) => void
    totalCount?: number
    sx?: SxProps
}

export const C1TablePagination: FC<C1TablePaginationProps> = ({
    page,
    pageSize,
    data,
    canLoadMore = false,
    isLoading = false,
    onPageChange,
    totalCount,
    sx,
}) => {
    const intl = useIntl()

    const hasNextPage = useMemo(
        () =>
            ((page + 1 < data?.length) ? true : (page + 1 === data?.length && canLoadMore)),
        [data, page, canLoadMore],
    )

    const itemsInCache = useMemo(
        () => data.reduce((all, p) => all + p.length, 0),
        [data],
    )

    const defaultLabelDisplayedRows = useCallback(
        ({ from: _from, to: _to }) => {
            if (isLoading) {
                return intl.formatMessage(
                    {
                        id: 'component_c1_table_pagination',
                        defaultMessage: 'Loading...',
                    },
                )
            }

            // Calculate pagination values correctly based on page
            const startIndex = page * pageSize + 1
            const endIndex = Math.min((page + 1) * pageSize, itemsInCache)

            // If we're at the last page, or if we have a static total count, we can show it.
            // Otherwise, we need to show an "estimated" count.
            return (!hasNextPage || totalCount) && data
                ? intl.formatMessage(
                    {
                        id: 'component_c1_table_pagination_with_total',
                        defaultMessage: '{from}–{to} of {len}',
                    },
                    {
                        from: intl.formatNumber(startIndex),
                        to: intl.formatNumber(endIndex),
                        len: intl.formatNumber(totalCount ?? itemsInCache),
                    },
                )
                : intl.formatMessage(
                    {
                        id: 'component_c1_table_pagination_unknown_total',
                        defaultMessage: '{from}–{to} of more than {to}',
                    },
                    {
                        from: intl.formatNumber(startIndex),
                        to: intl.formatNumber(endIndex),
                    },
                )
        },
        [itemsInCache, data, intl, hasNextPage, isLoading, page, pageSize, totalCount],
    )

    const getItemAriaLabel = useCallback((type: 'first' | 'last' | 'next' | 'previous') => {
        switch (type) {
            case 'next':
                return intl.formatMessage({ id: 'component_pagination_next_page', defaultMessage: 'Go to next page' })
            case 'previous':
                return intl.formatMessage({ id: 'component_pagination_previous_page', defaultMessage: 'Go to previous page' })
            case 'first':
                return intl.formatMessage({ id: 'component_pagination_first_page', defaultMessage: 'Go to first page' })
            case 'last':
                return intl.formatMessage({ id: 'component_pagination_last_page', defaultMessage: 'Go to last page' })
            default:
                return ''
        }
    }, [intl])

    if (itemsInCache === 0) {
        return null
    }

    return <TablePagination
        component="div"
        page={page}
        rowsPerPageOptions={[pageSize]}
        count={totalCount ?? -1}
        rowsPerPage={pageSize}
        labelDisplayedRows={defaultLabelDisplayedRows}
        getItemAriaLabel={getItemAriaLabel}
        onPageChange={(event, newPage): void => {
            onPageChange(newPage)
        }}
        sx={sx || C1TableReduxStyles.tablePagination}
        nextIconButtonProps={{
            size: 'medium',
            disabled: !hasNextPage || isLoading,
            'aria-label': getItemAriaLabel('next'),
        }}
        backIconButtonProps={{
            size: 'medium',
            disabled: page === 0 || isLoading,
            'aria-label': getItemAriaLabel('previous'),
        }}
    />
}
